import AiIcon from "../../Core/AiIcon/AiIcon";
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
	CHART_TYPE_LABELS,
	CHART_TYPES,
	ChartType,
	ComparisonMode,
	GRAIN_LABELS,
	LakeQueryResponse,
	ReportFilters,
	ReportWidget,
	WidgetSpec,
} from "../../../models/ReportBuilder";
import { useGenerateWidget } from "../../../networking/report-builder.hooks";
import { presetLabel } from "./dateRange";
import WidgetChart from "./WidgetChart";

interface WidgetDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Set when refining an existing widget. */
	widget?: ReportWidget;
	initialPrompt?: string;
	range: { from: string; to: string };
	compare?: ComparisonMode;
	filters?: ReportFilters;
	onSave: (spec: WidgetSpec, prompt: string) => void;
}

/**
 * Prompt → generated widget → preview → save. The backend makes the SQL and
 * chart definition; the user can still override the chart type here.
 */
const WidgetDialog: React.FC<WidgetDialogProps> = ({
	open,
	onOpenChange,
	widget,
	initialPrompt,
	range,
	compare,
	filters,
	onSave,
}) => {
	const [prompt, setPrompt] = useState("");
	const [spec, setSpec] = useState<WidgetSpec | undefined>();
	const [preview, setPreview] = useState<LakeQueryResponse | undefined>();
	const [previewError, setPreviewError] = useState<string | undefined>();
	/** Set once the backend has produced a spec for the text in the box; saving without it would keep the old widget. */
	const [generatedFor, setGeneratedFor] = useState<string | undefined>();
	const generate = useGenerateWidget();

	useEffect(() => {
		if (!open) return;
		setPrompt(initialPrompt ?? "");
		setSpec(widget?.spec);
		setPreview(undefined);
		setPreviewError(undefined);
		setGeneratedFor(undefined);
		generate.reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, widget?.id, initialPrompt]);

	const run = () => {
		const text = prompt.trim();
		if (!text) return;
		generate.mutate(
			{
				prompt: text,
				current: widget?.spec,
				from: range.from,
				to: range.to,
				compare,
				bucket: widget?.spec.bucket,
				filters,
			},
			{
				onSuccess: (result) => {
					setSpec(result.spec);
					setPreview(result.preview);
					setPreviewError(result.previewError);
					setGeneratedFor(text);
				},
			}
		);
	};

	const changeType = (type: ChartType) =>
		spec && setSpec({ ...spec, chart: { ...spec.chart, type } });

	// Typed text that the backend has not seen yet must be generated before saving.
	const needsGeneration = !!prompt.trim() && generatedFor !== prompt.trim();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="z-[1300] flex max-h-[90vh] w-[min(56rem,calc(100vw-2rem))] max-w-none flex-col overflow-hidden">
				<DialogHeader>
					<DialogTitle>{widget ? "Ändra widget" : "Ny widget"}</DialogTitle>
					<DialogDescription className="break-words">
						{widget
							? `Beskriv ändringen. Widgeten skapades med: "${widget.prompt}". `
							: "Beskriv vad widgeten ska visa. Perioden väljs på dashboarden, så skriv inte in datum. "}
						Förhandsvisningen använder {range.from} – {range.to}. Enter skapar, Shift+Enter ger ny
						rad.
					</DialogDescription>
				</DialogHeader>

				<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
					<textarea
						value={prompt}
						onChange={(e) => setPrompt(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								run();
							}
						}}
						rows={3}
						placeholder={
							widget
								? "T.ex. visa även kostnad, eller byt till per vecka"
								: "T.ex. omsättning och bruttovinst per månad, eller de tio kunderna med flest timmar"
						}
						className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-brand focus:outline-none"
					/>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={run}
							disabled={generate.isLoading || !prompt.trim()}
							className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
						>
							{generate.isLoading ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<AiIcon className="h-4 w-4" />
							)}
							{widget ? "Uppdatera" : "Skapa"} (⏎)
						</button>
						{spec && (
							<Select value={spec.chart.type} onValueChange={(v) => changeType(v as ChartType)}>
								<SelectTrigger className="w-44">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="z-[1300]">
									{CHART_TYPES.map((t) => (
										<SelectItem key={t} value={t}>
											{CHART_TYPE_LABELS[t]}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
						{generate.error && (
							<span className="min-w-0 break-words text-xs text-red-600">
								{generate.error.error?.detail ||
									generate.error.error?.title ||
									"Kunde inte skapa widget"}
							</span>
						)}
					</div>

					{spec && (
						<div className="min-w-0 rounded-lg border border-gray-200 p-3">
							<div className="mb-2 text-sm font-semibold">{spec.title}</div>
							{spec.description && (
								<div className="mb-2 text-xs text-gray-500">{spec.description}</div>
							)}
							{spec.period && (
								<div className="mb-2 text-xs font-medium text-action">
									Egen period: {presetLabel(spec.period)} – widgeten följer inte dashboardens
									periodval (ändras i widgetmenyn).
								</div>
							)}
							{spec.bucket && spec.bucket !== "auto" && (
								<div className="mb-2 text-xs font-medium text-gray-600">
									Låst gruppering: per {GRAIN_LABELS[spec.bucket].toLowerCase()} (ändras under Tid i
									widgetmenyn).
								</div>
							)}
							{spec.usesBucket && (!spec.bucket || spec.bucket === "auto") && (
								<div className="mb-2 text-xs text-gray-500">
									Tidsaxeln grupperas automatiskt efter periodens längd; låses under Tid i
									widgetmenyn.
								</div>
							)}
							{preview && (
								<div className="min-w-0 overflow-x-auto" style={{ height: 220 }}>
									<WidgetChart spec={spec} data={preview} height={220} />
								</div>
							)}
							{!preview && !previewError && widget && (
								<div className="text-xs text-gray-500">
									Skriv en ändring och tryck Uppdatera för ny förhandsvisning.
								</div>
							)}
							{previewError && (
								<div className="break-words rounded bg-red-50 p-2 text-xs text-red-700">
									Förhandsvisningen misslyckades: {previewError}
								</div>
							)}
							<details className="mt-2">
								<summary className="cursor-pointer text-xs text-gray-500">SQL</summary>
								<pre className="mt-1 max-h-60 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-900 p-2 text-[11px] leading-snug text-gray-100">
									{spec.sql}
								</pre>
							</details>
						</div>
					)}
				</div>

				<DialogFooter className="items-center gap-2">
					{needsGeneration && (
						<span className="mr-auto text-xs text-gray-500">
							Tryck {widget ? "Uppdatera" : "Skapa"} så att ändringen körs innan du sparar.
						</span>
					)}
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="rounded-md border px-3 py-1.5 text-sm"
					>
						Avbryt
					</button>
					<button
						type="button"
						disabled={!spec || needsGeneration}
						onClick={() => spec && onSave(spec, generatedFor ?? widget?.prompt ?? prompt.trim())}
						className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
					>
						{widget ? "Spara ändring" : "Lägg till på dashboarden"}
					</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default WidgetDialog;
